import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from './Header';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import toast from 'react-hot-toast';
import './ColoCalculator.css';

const ColoCalculator = () => {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [units, setUnits] = useState(1);
  const [voltage, setVoltage] = useState(120);
  const [network, setNetwork] = useState(0);
  const [amps, setAmps] = useState(1);
  const [ipCount, setIpCount] = useState(0);
  const [hasFirewall, setHasFirewall] = useState(false);
  const [loading, setLoading] = useState(false);

  // Calculate costs
  const unitsCost = 100 + (units - 1) * 10;
  const networkCost = network * 155;
  const actualAmps = Math.min(amps, 5);
  const ampsCost = voltage === 208 ? actualAmps * 46.5 : actualAmps * 46.5 * (120 / 208);
  const ipCost = Math.min(ipCount, 10) * 5;
  const firewallCost = hasFirewall ? 100 : 0;
  const totalMonthly = unitsCost + networkCost + ampsCost + ipCost + firewallCost;

  // Build line items
  const lineItems = [];
  
  // U cost line
  const uLabel = units === 1 ? '1 U' : `${units} U`;
  lineItems.push({ label: uLabel, price: `$${unitsCost.toFixed(2)}` });

  // Network line
  if (network === 0) {
    lineItems.push({ label: '1 Gbps Shared Network', price: 'Included' });
  } else {
    lineItems.push({ label: `${network} Gbps Dedicated Network`, price: `$${networkCost.toFixed(2)}` });
  }

  // Power line
  const ampLabel = amps > 5 ? '5+ Amps' : (actualAmps === 1 ? '1 Amp' : `${actualAmps} Amps`);
  lineItems.push({ label: `${ampLabel} (${voltage}V)`, price: `$${ampsCost.toFixed(2)}` });

  // IP line
  if (ipCount > 0) {
    const actualIPs = Math.min(ipCount, 10);
    const ipLabel = actualIPs === 1 ? '1 Additional IP' : `${actualIPs} Additional IPs`;
    lineItems.push({ label: ipLabel, price: `$${ipCost.toFixed(2)}` });
  } else {
    lineItems.push({ label: '1 IP Address', price: 'Included' });
  }

  if (ipCount > 10) {
    lineItems.push({ label: '10+ IPs - Contact for quote', price: '', warning: true });
  }

  // Firewall line
  if (hasFirewall) {
    lineItems.push({ label: '1 U Firewall (includes power & 1 IP)', price: `$${firewallCost.toFixed(2)}` });
  }

  const handleCheckout = async () => {
    if (!isAuthenticated || !user) {
      toast('Please sign up or log in to subscribe.', { icon: '🔒', duration: 4000 });
      navigate('/login?redirect=/colo-calculator');
      return;
    }
    // Validate that total is greater than 0
    if (totalMonthly <= 0) {
      toast.error('Please configure your colo setup before proceeding to checkout');
      return;
    }

    // Check if user needs to contact for custom quote
    if (amps > 5 || ipCount > 10) {
      toast.error('Please contact sales@inlandfiber.com for a custom quote');
      return;
    }

    setLoading(true);
    try {
      const coloConfig = {
        units,
        voltage,
        network,
        amps,
        ipCount,
        hasFirewall,
      };

      const response = await api.post('/api/stripe/create-colo-checkout-session', {
        totalMonthly,
        lineItems,
        coloConfig,
      });

      // Redirect to Stripe Checkout
      if (response.data.url) {
        window.location.href = response.data.url;
      } else {
        toast.error('Failed to create checkout session');
        setLoading(false);
      }
    } catch (error) {
      if (error.response?.status === 401) {
        toast('Please sign up or log in to subscribe.', { icon: '🔒', duration: 4000 });
        navigate('/login?redirect=/colo-calculator');
        setLoading(false);
        return;
      }
      console.error('Checkout error:', error);
      console.error('Error response:', error.response?.data);
      const errorMessage = error.response?.data?.message || error.response?.data?.error || error.message || 'Failed to start checkout';
      toast.error(errorMessage);
      setLoading(false);
    }
  };

  return (
    <div className="colo-calculator-page">
      <Header />
      <div className="colo-container">
        <div className="colo-calculator">
          <h1>
            Inland Fiber Colo By The U Calculator<br />(single server)
          </h1>

          <div className="slider-group">
            <label htmlFor="units">Number of U ($100 first U)</label>
            <div className="slider-row">
              <div className="slider-wrapper" style={{ '--fill-percent': `${((units - 1) / (4 - 1)) * 100}%` }}>
                <div className="slider-track-row">
                  <div className="slider-track slider-track-bg" aria-hidden="true" />
                  <div className="slider-track slider-track-fill" aria-hidden="true" />
                  <input
                    type="range"
                    id="units"
                    min="1"
                    max="4"
                    value={units}
                    step="1"
                    onChange={(e) => setUnits(parseInt(e.target.value))}
                    list="units-ticks"
                  />
                </div>
                <datalist id="units-ticks">
                  <option value="1"></option>
                  <option value="2"></option>
                  <option value="3"></option>
                  <option value="4"></option>
                </datalist>
              </div>
              <span className="value-display">{units} U</span>
            </div>
          </div>

          <div className="slider-group">
            <label htmlFor="voltage">Voltage</label>
            <div className="slider-row">
              <select
                id="voltage"
                value={voltage}
                onChange={(e) => setVoltage(parseInt(e.target.value))}
                style={{
                  flex: 1,
                  padding: '10px 12px',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: '8px',
                  fontSize: '1em',
                  background: 'rgba(17, 24, 39, 0.8)',
                  color: '#f9fafb',
                  cursor: 'pointer',
                }}
              >
                <option value="120">120V</option>
                <option value="208">208V</option>
              </select>
            </div>
          </div>

          <div className="slider-group">
            <label htmlFor="amps">Power (Amps)</label>
            <div className="slider-row">
              <div className="slider-wrapper" style={{ '--fill-percent': `${((amps - 1) / (6 - 1)) * 100}%` }}>
                <div className="slider-track-row">
                  <div className="slider-track slider-track-bg" aria-hidden="true" />
                  <div className="slider-track slider-track-fill" aria-hidden="true" />
                  <input
                    type="range"
                    id="amps"
                    min="1"
                    max="6"
                    value={amps}
                    step="1"
                    onChange={(e) => setAmps(parseInt(e.target.value))}
                    list="amps-ticks"
                  />
                </div>
                <datalist id="amps-ticks">
                  <option value="1"></option>
                  <option value="2"></option>
                  <option value="3"></option>
                  <option value="4"></option>
                  <option value="5"></option>
                  <option value="6"></option>
                </datalist>
              </div>
              <span className="value-display">
                {amps > 5 ? '5+ Amps' : `${amps} ${amps === 1 ? 'Amp' : 'Amps'}`}
              </span>
            </div>
            {amps > 5 && (
              <div className="custom-quote-amps">
                For more than 5 amps, please email{' '}
                <a href="mailto:sales@inlandfiber.com">sales@inlandfiber.com</a> for a custom quote.
              </div>
            )}
          </div>

          <div className="slider-group">
            <label htmlFor="network">Network Speed</label>
            <div className="slider-row">
              <div className="slider-wrapper" style={{ '--fill-percent': `${(network / 10) * 100}%` }}>
                <div className="slider-track-row">
                  <div className="slider-track slider-track-bg" aria-hidden="true" />
                  <div className="slider-track slider-track-fill" aria-hidden="true" />
                  <input
                    type="range"
                    id="network"
                    min="0"
                    max="10"
                    value={network}
                    step="1"
                    onChange={(e) => setNetwork(parseInt(e.target.value))}
                    list="network-ticks"
                  />
                </div>
                <datalist id="network-ticks">
                  <option value="0"></option>
                  <option value="1"></option>
                  <option value="2"></option>
                  <option value="3"></option>
                  <option value="4"></option>
                  <option value="5"></option>
                  <option value="6"></option>
                  <option value="7"></option>
                  <option value="8"></option>
                  <option value="9"></option>
                  <option value="10"></option>
                </datalist>
              </div>
              <span className="value-display">
                {network === 0 ? '1 Gbps Shared' : `${network} Gbps Dedicated`}
              </span>
            </div>
          </div>

          <div className="slider-group">
            <label htmlFor="ips">Additional IP Addresses (one included - $5/each)</label>
            <div className="slider-row">
              <div className="slider-wrapper" style={{ '--fill-percent': `${(ipCount / 11) * 100}%` }}>
                <div className="slider-track-row">
                  <div className="slider-track slider-track-bg" aria-hidden="true" />
                  <div className="slider-track slider-track-fill" aria-hidden="true" />
                  <input
                    type="range"
                    id="ips"
                    min="0"
                    max="11"
                    value={ipCount}
                    step="1"
                    onChange={(e) => setIpCount(parseInt(e.target.value))}
                    list="ips-ticks"
                  />
                </div>
                <datalist id="ips-ticks">
                  <option value="0"></option>
                  <option value="1"></option>
                  <option value="2"></option>
                  <option value="3"></option>
                  <option value="4"></option>
                  <option value="5"></option>
                  <option value="6"></option>
                  <option value="7"></option>
                  <option value="8"></option>
                  <option value="9"></option>
                  <option value="10"></option>
                  <option value="11"></option>
                </datalist>
              </div>
              <span className="value-display">{ipCount > 10 ? '10+' : ipCount}</span>
            </div>
            {ipCount > 10 && (
              <div className="custom-quote">
                For more than 10 IP addresses, please email{' '}
                <a href="mailto:sales@inlandfiber.com">sales@inlandfiber.com</a> for a custom quote.
              </div>
            )}
          </div>

          <div className="slider-group">
            <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', userSelect: 'none' }}>
              <input
                type="checkbox"
                id="firewall"
                checked={hasFirewall}
                onChange={(e) => setHasFirewall(e.target.checked)}
                style={{ width: 'auto', marginRight: '10px', cursor: 'pointer' }}
              />
              <span>Customer provided 1 U firewall</span>
            </label>
            <div style={{ marginLeft: '30px', marginTop: '8px', fontSize: '0.9em', color: '#9ca3af' }}>
              Includes power and 1 IP address
            </div>
          </div>

          <div className="sales-support">
            <h3>Sales Support</h3>
            <p>
              <strong>Email:</strong>{' '}
              <a href="mailto:sales@inlandfiber.com">sales@inlandfiber.com</a>
            </p>
            <p>
              <strong>Phone:</strong>{' '}
              <a href="tel:863-256-0010">863-256-0010</a>
            </p>
          </div>
        </div>

        <div className="result">
          <div className="plan-name">Colo By The U</div>
          <div id="line-items">
            {lineItems.map((item, index) => (
              <div
                key={index}
                className="line-item"
                style={item.warning ? { color: '#856404', fontStyle: 'italic' } : {}}
              >
                <span className="label">{item.label}</span>
                <span className="price">{item.price}</span>
              </div>
            ))}
          </div>
          <div className="total-section">
            <div className="total-row">
              <span>Total</span>
              <span id="monthly-price">${totalMonthly.toFixed(2)}/mo</span>
            </div>
            <div className="smart-hands-info">
              <strong>Smart Hands: </strong>Billed in 30 minute increments<br />
              $150/hr Business hours (M-F 10AM-6PM EST)<br />
              $250/hr after hours and Holidays
            </div>
          </div>
          <button
            className="submit-button"
            id="submit-btn"
            onClick={handleCheckout}
            disabled={loading || totalMonthly <= 0 || amps > 5 || ipCount > 10}
          >
            {loading ? 'Processing...' : 'Proceed to Checkout'}
          </button>
          {(amps > 5 || ipCount > 10) && (
            <div className="order-info show" style={{ marginTop: '20px' }}>
              <p style={{ color: '#856404', fontStyle: 'italic' }}>
                <strong>Note:</strong> For configurations with more than 5 amps or 10+ IP addresses, 
                please contact <a href="mailto:sales@inlandfiber.com">sales@inlandfiber.com</a> for a custom quote.
              </p>
            </div>
          )}
          <div className="order-info" style={{ marginTop: '20px', fontSize: '0.9em', color: '#d1d5db' }}>
            <h3>Shipping Information</h3>
            <p>
              <strong>After checkout, please ensure you ship the server(s), power cables and rails to:</strong>
            </p>
            <p>
              Inland Fiber and Data<br />
              STE 500<br />
              199 Ave B NW<br />
              Winter Haven, Florida 33881
            </p>
            <p>
              <strong>Important:</strong> Please pre-configure your server(s) with the IP Address
              Information that you will receive from Support@InlandFiber.com{' '}
              <b>BEFORE SHIPPING YOUR SERVER</b>
            </p>
            <p style={{ color: '#856404' }}>
              <strong>Note:</strong> Failure to do so could result in you having to use our
              billable smarthands service to fix network issues.
            </p>
            <p style={{ color: '#357b49', fontWeight: 'bold' }}>
              You will not be billed until your servers are racked and powered on.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ColoCalculator;

